import type { DocCollection, DocMeta } from '@blocksuite/store';
import { Button, Modal } from '@pique/component';
import type { Collection } from '@pique/env/filter';
import { useI18n } from '@pique/i18n';
import type { DialogContentProps } from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import * as styles from './edit-collection.css';
import { SelectPage } from './select-page';

export interface EditCollectionModalProps {
  init?: Collection;
  title?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (view: Collection) => void;
}

const contentOptions: DialogContentProps = {
  style: {
    padding: 0,
    maxWidth: 944,
    backgroundColor: 'var(--affine-background-primary-color)',
  },
};
export const EditCollectionModal = ({
  init,
  onConfirm,
  open,
  onOpenChange,
  title,
}: EditCollectionModalProps) => {
  const t = useI18n();
  const onConfirmOnCollection = useCallback(
    (view: Collection) => {
      onConfirm(view);
      onOpenChange(false);
    },
    [onConfirm, onOpenChange]
  );
  const onCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  if (!(open && init)) {
    return null;
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      withoutCloseButton
      width="calc(100% - 64px)"
      height="80%"
      contentOptions={contentOptions}
      persistent
    >
      <EditCollection
        title={title}
        onConfirmText={t['com.pique.editCollection.save']()}
        init={init}
        onCancel={onCancel}
        onConfirm={onConfirmOnCollection}
      />
    </Modal>
  );
};

export interface EditCollectionProps {
  title?: string;
  onConfirmText?: string;
  init: Collection;
  onCancel: () => void;
  onConfirm: (collection: Collection) => void;
}

export const EditCollection = ({
  init,
  onConfirm,
  onCancel,
  onConfirmText,
}: EditCollectionProps) => {
  const t = useI18n();
  const [value, onChange] = useState<Collection>(init);
  const isNameEmpty = useMemo(() => value.name.trim().length === 0, [value]);
  const onSaveCollection = useCallback(() => {
    if (!isNameEmpty) {
      onConfirm(value);
    }
  }, [value, isNameEmpty, onConfirm]);
  const onIdsChange = useCallback(
    (ids: string[]) => {
      onChange({ ...value, allowList: ids });
    },
    [value]
  );
  const onFiltersChange = useCallback(
    (filters: Collection['filterList']) => {
      onChange({ ...value, filterList: filters });
    },
    [value]
  );
  const buttons = useMemo(
    () => (
      <>
        <Button onClick={onCancel} className={styles.actionButton}>
          {t['com.pique.editCollection.button.cancel']()}
        </Button>
        <Button
          className={styles.actionButton}
          data-testid="save-collection"
          variant="primary"
          disabled={isNameEmpty}
          onClick={onSaveCollection}
        >
          {onConfirmText ?? t['com.pique.editCollection.button.create']()}
        </Button>
      </>
    ),
    [onCancel, t, isNameEmpty, onSaveCollection, onConfirmText]
  );
  return (
    <div
      onKeyDown={e => {
        if (e.key === 'Escape') {
          return;
        }
        e.stopPropagation();
      }}
      className={styles.collectionEditContainer}
    >
      <SelectPage
        init={value.allowList}
        initFilters={value.filterList}
        onChange={onIdsChange}
        onFiltersChange={onFiltersChange}
        buttons={buttons}
      />
    </div>
  );
};

export type AllPageListConfig = {
  allPages: DocMeta[];
  docCollection: DocCollection;
  /**
   * Return `undefined` if the page is not public
   */
  getPublicMode: (id: string) => undefined | 'page' | 'edgeless';
  getPage: (id: string) => DocMeta | undefined;
  favoriteRender: (page: DocMeta) => ReactNode;
};
